"use client";

import { router } from "@inertiajs/react";
import { ChevronDown, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import AsyncCombobox from "@/components/async-combobox";
import { CompanyFilterSelect } from "@/components/company-filter-select";
import { DataTablePagination } from "@/components/data-table-pagination";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PURCHASE_REQUEST_STATUS_OPTIONS } from "@/constants/purchase-request";
import { useDataTable } from "@/hooks/use-data-table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { useSectorFilter } from "@/hooks/use-sector-filter";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import { DataTable } from "@/lib/data-table";
import type {
  Company,
  PagyMetadata,
  PurchaseRequest,
  RansackFilters,
} from "@/types";

import { columns } from "./columns";

interface PurchaseRequestsDataTableProps {
  purchaseRequests: PurchaseRequest[];
  pagy: PagyMetadata;
  filters: RansackFilters;
  companies: Company[];
}

const STATUSES = PURCHASE_REQUEST_STATUS_OPTIONS;

export function PurchaseRequestsDataTable({
  purchaseRequests,
  pagy,
  filters,
  companies,
}: PurchaseRequestsDataTableProps) {
  const routes = useOrganizationRoutes();

  const {
    isLoading,
    handleFilterChange,
    handlePageChange,
    handleSortingChange,
    getSortingState,
  } = useDataTable({
    filters,
    pagy,
    path: routes.purchaseRequests.index(),
  });

  const [searchMode, setSearchMode] = useState<"number" | "buyer">("number");
  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useSyncedDebouncedValue<string>(
    String(filters.sequential_id_eq ?? filters.with_buyer_search ?? ""),
  );
  const [statusValue, setStatusValue] = useState(filters.status_eq ?? "all");
  const [companyValue, setCompanyValue] = useState(
    filters.sector_company_id_eq ?? "all",
  );

  const {
    sectorOption,
    loadSectorOptions,
    handleSectorSelect,
    resetSector,
    hasSectorFilter,
  } = useSectorFilter({
    filters,
    handleFilterChange,
    searchEndpoint: routes.sectors.search(),
  });

  useEffect(() => {
    setStatusValue(filters.status_eq ?? "all");
  }, [filters.status_eq]);

  useEffect(() => {
    setCompanyValue(filters.sector_company_id_eq ?? "all");
  }, [filters.sector_company_id_eq]);

  useEffect(() => {
    if (filters.sequential_id_eq) {
      setSearchMode("number");
    } else if (filters.with_buyer_search) {
      setSearchMode("buyer");
    }
  }, [filters.sequential_id_eq, filters.with_buyer_search]);

  // Debounced search to avoid excessive server requests
  useEffect(() => {
    const normalized = debouncedSearchValue.trim();
    const nextValue = normalized ? normalized : undefined;

    if (searchMode === "number") {
      if ((filters.sequential_id_eq ?? undefined) !== nextValue) {
        handleFilterChange({
          sequential_id_eq: nextValue,
          with_buyer_search: undefined,
        });
      }
    } else {
      if ((filters.with_buyer_search ?? undefined) !== nextValue) {
        handleFilterChange({
          with_buyer_search: nextValue,
          sequential_id_eq: undefined,
        });
      }
    }
    // Only depend on debounced value + searchMode to avoid loops when filters change from the server.
  }, [debouncedSearchValue, searchMode]);

  const handleReset = () => {
    setSearchValue("");
    setStatusValue("all");
    setCompanyValue("all");
    resetSector();
    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  const handleStatusChange = (value: string) => {
    setStatusValue(value);
    handleFilterChange({ status_eq: value === "all" ? undefined : value });
  };

  const handleCompanyChange = (value: string) => {
    setCompanyValue(value);
    handleFilterChange({
      sector_company_id_eq: value === "all" ? undefined : value,
    });
  };

  const hasSearchFilter = Boolean(searchValue);
  const hasStatusFilter = statusValue !== "all";
  const hasCompanyFilter = companyValue !== "all";

  const hasActiveFilters =
    hasSearchFilter || hasStatusFilter || hasSectorFilter || hasCompanyFilter;

  // External toolbar - completely separate from TanStack
  const renderExternalToolbar = () => (
    <div className="flex flex-wrap items-center gap-3">
      <ButtonGroup>
        <InputGroup className="w-[280px]">
          <InputGroupInput
            placeholder={
              searchMode === "number"
                ? "Buscar por ID/Nº..."
                : "Buscar por comprador..."
            }
            type={searchMode === "number" ? "number" : "text"}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
          <InputGroupAddon align="inline-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <InputGroupButton variant="ghost" className="!pr-1.5 text-xs">
                  {searchMode === "number" ? "ID" : "Comprador"}{" "}
                  <ChevronDown className="size-3" />
                </InputGroupButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSearchMode("number")}>
                  ID/Nº Solicitud
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSearchMode("buyer")}>
                  Comprador
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </InputGroupAddon>
        </InputGroup>
        <AsyncCombobox
          value={sectorOption}
          onChange={handleSectorSelect}
          loadOptions={loadSectorOptions}
          defaultOptions={true}
          cacheOptions={true}
          placeholder="Filtrar por sector"
          buttonClassName="w-[200px] rounded-none border-l-0"
        />
        <CompanyFilterSelect
          value={companyValue}
          onChange={handleCompanyChange}
          companies={companies}
          className="w-[220px] rounded-none border-l-0"
        />
        <Select value={statusValue} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px] rounded-l-none border-l-0">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ButtonGroup>
      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={handleReset}
          className="h-9 px-2 lg:px-3"
        >
          <Trash2 className="h-4 w-4" /> Limpiar filtros
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* External toolbar - no TanStack filtering */}
      {renderExternalToolbar()}

      {/* Staleness explanation */}
      <div className="flex items-center gap-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-200">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-yellow-300 dark:bg-yellow-600" />
          <span className="font-medium">Filas amarillas:</span>
          <span>Solicitudes sin actividad durante 2 o más días</span>
        </div>
      </div>

      <div className={isLoading ? "opacity-50" : ""}>
        <DataTable
          columns={columns}
          data={purchaseRequests}
          sorting={getSortingState()}
          onSortingChange={handleSortingChange}
          manualSorting={true}
          manualPagination={true}
          pageCount={pagy.pages}
          pagination={false} // We'll use our custom pagination
          onRowClick={(purchaseRequest) =>
            router.visit(routes.purchaseRequests.show(purchaseRequest.id))
          }
          rowClassName={(purchaseRequest) => {
            const status = purchaseRequest.staleness_status;
            if (status === "stale" || status === "warning") {
              return "bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200/80 dark:hover:bg-yellow-900/50";
            }
            return undefined;
          }}
        />
      </div>

      {/* Server-side pagination */}
      <DataTablePagination
        pagy={pagy}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        entityName="solicitudes"
      />
    </div>
  );
}
